import { forwardRef, useImperativeHandle, useState } from 'react';

import { CheckedState } from '@radix-ui/react-checkbox';
import { Checkbox, Flex, Text } from '@raystack/apsara';
import { Modal } from '@studio/components';

import { For, Show } from '@src/hoc';
import { downloadAsset } from '@src/utils/helpers/assetHelpers';

import styles from './DownloadAoiAssetPopup.module.css';

type Asset = { name: string; id: string; aoi_id: string };

interface DownloadAoiAssetPopup {
  assets: Asset[];
  fileName: string;
  startDownloadFn: (
    aoi_id: string,
    asset_ids: string[]
  ) => ReturnType<Parameters<typeof downloadAsset>[0]['startDownloadFn']>;
  fetchStatusFn: (
    aoi_id: string,
    download_id: string
  ) => ReturnType<Parameters<typeof downloadAsset>[0]['fetchStatusFn']>;
}

interface DownloadAoiAssetPopupRef {
  setShowDialog: () => void;
}

export const DownloadAoiAssetPopup = forwardRef<
  DownloadAoiAssetPopupRef,
  DownloadAoiAssetPopup
>(
  (
    {
      assets = [],
      fileName,
      startDownloadFn,
      fetchStatusFn
    }: DownloadAoiAssetPopup,
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useImperativeHandle(
      ref,
      () => ({ setShowDialog: () => setOpen(true) }),
      []
    );

    const exportOptions = [
      { id: 'input', label: 'Input', when: !!assets.length, options: assets }
    ];

    const [resultById, setResultById] = useState(
      exportOptions.reduce<Record<string, Set<string>>>((acc, curr) => {
        acc[curr.id] = new Set();
        return acc;
      }, {})
    );

    function setResult(id: string, value: string, isChecked: CheckedState) {
      setResultById(d => {
        if (isChecked) {
          d[id].add(value);
        } else {
          d[id].delete(value);
        }

        return { ...d };
      });
    }

    function handleExportClick() {
      setIsLoading(true);

      const asset_ids = Object.values(resultById)
        .map(d => [...d])
        .reduce((acc, curr) => {
          acc.push(...curr);
          return acc;
        }, []);

      const { aoi_id } = assets[0];

      downloadAsset({
        fileName,
        onSuccess: () => setOpen(false),
        onFinally: () => setIsLoading(false),
        startDownloadFn: () => startDownloadFn(aoi_id, asset_ids),
        fetchStatusFn: download_id => fetchStatusFn(aoi_id, download_id)
      });
    }

    return (
      <Modal
        show={open}
        title='Export'
        buttonText='Export'
        loaderText='Preparing for download'
        loading={isLoading}
        disabled={
          isLoading ||
          Object.values(resultById)
            .map(d => d.size)
            .reduce((acc, curr) => {
              acc += curr;
              return acc;
            }, 0) === 0
        }
        onClose={() => setOpen(false)}
        onClick={handleExportClick}
        data-test-id='export-btn'
      >
        <Flex className={styles.dialogBody} direction='column' gap='small'>
          <For each={exportOptions}>
            {exportOption => (
              <Show when={exportOption.when}>
                <For each={exportOption.options}>
                  {option => (
                    <Flex gap='small' align='center'>
                      <Checkbox
                        checked={resultById[exportOption.id].has(option.id)}
                        onCheckedChange={(check: CheckedState) => {
                          setResult(exportOption.id, option.id, check);
                        }}
                        id={option.id}
                      />
                      <Text size={2} className={styles.dialogOptions}>
                        {option.name}
                      </Text>
                    </Flex>
                  )}
                </For>
              </Show>
            )}
          </For>
        </Flex>
      </Modal>
    );
  }
);

DownloadAoiAssetPopup.displayName = 'DownloadAoiAssetPopup';
